import React from 'react';
import {
  Avatar,
  Box,
  createToaster,
  Icon,
  Menu,
  Portal,
  Separator,
  Text,
  Toast,
  Toaster,
} from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  LuAtSign,
  LuCreditCard,
  LuEllipsis,
  LuIdCard,
  LuLogOut,
  LuPencil,
  LuUser,
} from 'react-icons/lu';
import { useAuth } from '@kuow/shared-ui';
import {
  logger,
  setUserProfileImage,
  updateMyShippingAddress,
  updateUserPassword,
  uploadProfileImage,
} from '@kuow/core';
import type { Address } from '@kuow/core';
import { EditAddressDialog, type AddressCountry, type AddressFormValue } from './EditAddressDialog';
import { EditPasswordDialog } from './EditPasswordDialog';

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png'] as const;
const MAX_IMAGE_BYTES = 15 * 1024 * 1024;
const FILE_INPUT_ACCEPT = ALLOWED_IMAGE_TYPES.join(',');

const toaster = createToaster({ placement: 'top' });

const ITEM_TEXT_PROPS = {
  fontSize: 'sm',
  lineHeight: '20px',
  flex: '1 0 0',
  minW: 0,
} as const;

const SUBTEXT_PROPS = {
  pl: '30px',
  fontSize: 'xs',
  fontWeight: 'medium',
  lineHeight: '16px',
  color: 'fg.muted',
  w: 'full',
} as const;

const formatAddressLines = (address: Address | undefined | null): string[] => {
  if (!address) return [];
  const street = address.street?.trim() ?? '';
  const city = address.city?.trim() ?? '';
  const state = address.state?.trim() ?? '';
  const zip = address.postal_code?.trim() ?? '';
  const stateZip = [state, zip].filter(Boolean).join(' ');
  const cityLine = city && stateZip ? `${city}, ${stateZip}` : city || stateZip;
  const lines: string[] = [];
  if (street) lines.push(street);
  if (cityLine) lines.push(cityLine);
  return lines;
};

const toAddressFormValue = (address: Address | undefined | null): Partial<AddressFormValue> => {
  if (!address) return {};
  const country: AddressCountry = address.country?.trim().toUpperCase() === 'CA' ? 'CA' : 'US';
  return {
    street: address.street ?? '',
    city: address.city ?? '',
    state: address.state ?? '',
    postalCode: address.postal_code ?? '',
    country,
  };
};

export const ProfileMenu: React.FC = () => {
  const { user, member, logout, refreshMember, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const onProfilePage = location.pathname.startsWith('/members/profile');
  const email = user?.email ?? '';
  const fullName = [user?.first_name, user?.last_name]
    .map((part) => part?.trim())
    .filter((part): part is string => Boolean(part && part.length > 0))
    .join(' ');
  const profileName = fullName.length > 0 ? fullName : email || 'Profile';
  const avatarUrl =
    user?.cdn_url && user.cdn_url.trim().length > 0 ? user.cdn_url.trim() : undefined;
  const shippingAddress = member?.shipping_address ?? null;
  const addressLines = formatAddressLines(shippingAddress);
  const addressInitialValue = toAddressFormValue(shippingAddress);

  const [passwordDialogOpen, setPasswordDialogOpen] = React.useState(false);
  const [addressDialogOpen, setAddressDialogOpen] = React.useState(false);
  const [avatarUploading, setAvatarUploading] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const handleSignOut = () => {
    logout();
    navigate('/', { replace: true });
  };

  const triggerAvatarPicker = () => {
    if (avatarUploading) return;
    fileInputRef.current?.click();
  };

  const handleAvatarFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    event.target.value = '';
    if (!file) return;

    if (!ALLOWED_IMAGE_TYPES.includes(file.type as typeof ALLOWED_IMAGE_TYPES[number])) {
      toaster.create({
        title: 'Unsupported image type',
        description: 'Please choose a JPEG or PNG image.',
        type: 'error',
      });
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toaster.create({
        title: 'Image too large',
        description: 'Please choose an image smaller than 15 MB.',
        type: 'error',
      });
      return;
    }
    if (!user?.id) {
      toaster.create({
        title: 'Not signed in',
        description: 'Please sign in again before changing your avatar.',
        type: 'error',
      });
      return;
    }

    setAvatarUploading(true);
    void (async () => {
      try {
        const upload = await uploadProfileImage(file);
        const profileImageId = typeof upload.id === 'string' ? upload.id : String(upload.id);
        await setUserProfileImage(user.id, profileImageId, file.name);
        await refreshUser();
        toaster.create({ title: 'Avatar updated', type: 'success' });
      } catch (error) {
        logger.error('Avatar upload failed', { userId: user.id, error });
        toaster.create({
          title: 'Unable to update avatar',
          description: error instanceof Error && error.message ? error.message : 'Please try again.',
          type: 'error',
        });
      } finally {
        setAvatarUploading(false);
      }
    })();
  };

  const handlePasswordSubmit = async (newPassword: string, newPasswordConfirm: string) => {
    if (!user?.id) {
      throw new Error('You must be signed in to change your password.');
    }
    try {
      await updateUserPassword(user.id, newPassword, newPasswordConfirm);
      toaster.create({ title: 'Password updated', type: 'success' });
    } catch (error) {
      logger.error('Failed to update password', { error });
      throw error;
    }
  };

  const handleAddressSubmit = async (next: AddressFormValue) => {
    if (!member?.id) {
      throw new Error('Member profile is not loaded yet. Please try again.');
    }
    try {
      await updateMyShippingAddress(member.id, {
        country: next.country,
        street: next.street,
        city: next.city,
        state: next.state,
        postal_code: next.postalCode,
      });
      await refreshMember();
    } catch (error) {
      logger.error('Failed to update shipping address', { error });
      throw error;
    }
  };

  return (
    <>
    <Menu.Root positioning={{ placement: 'bottom-end' }}>
      <Menu.Trigger asChild>
        <Box
          as="button"
          aria-label="Profile menu"
          bg="transparent"
          border="none"
          p="0"
          borderRadius="full"
          cursor="pointer"
          display="inline-flex"
          _focus={{ outline: 'none' }}
          _focusVisible={{
            outline: '2px solid',
            outlineColor: 'river.solid',
            outlineOffset: '4px',
          }}
        >
          <Avatar.Root
            size="sm"
            colorPalette="river"
            variant="solid"
            boxShadow="0 0 0 2px var(--chakra-colors-bg-panel), 0 0 0 4px var(--chakra-colors-river-solid)"
          >
            <Avatar.Fallback name={profileName} />
            {avatarUrl ? <Avatar.Image src={avatarUrl} alt={profileName} /> : null}
          </Avatar.Root>
        </Box>
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner zIndex="popover">
          <Menu.Content minW="280px" p="1" gap="2" borderRadius="l2">
            {!onProfilePage ? (
              <Menu.Item
                value="view-profile"
                cursor="pointer"
                onSelect={() => navigate('/members/profile')}
              >
                <Icon as={LuIdCard} boxSize="4" />
                <Text {...ITEM_TEXT_PROPS}>View Profile</Text>
              </Menu.Item>
            ) : null}

            <Menu.Item
              value="change-avatar"
              cursor="pointer"
              disabled={avatarUploading}
              onSelect={triggerAvatarPicker}
            >
              <Icon as={LuUser} boxSize="4" />
              <Text {...ITEM_TEXT_PROPS}>
                {avatarUploading ? 'Uploading…' : 'Change Avatar'}
              </Text>
            </Menu.Item>

            <Box w="full">
              <Menu.Item value="email" cursor="default" closeOnSelect={false}>
                <Icon as={LuAtSign} boxSize="4" />
                <Text {...ITEM_TEXT_PROPS}>Email Address</Text>
              </Menu.Item>
              {email ? <Text {...SUBTEXT_PROPS}>{email}</Text> : null}
            </Box>

            <Menu.Item
              value="set-password"
              cursor="pointer"
              onSelect={() => setPasswordDialogOpen(true)}
            >
              <Icon as={LuEllipsis} boxSize="4" />
              <Text {...ITEM_TEXT_PROPS}>Set Password</Text>
              <Icon as={LuPencil} boxSize="4" color="fg.muted" />
            </Menu.Item>

            <Separator borderColor="border" />

            <Box w="full">
              <Menu.Item
                value="address"
                cursor="pointer"
                onSelect={() => setAddressDialogOpen(true)}
              >
                <Icon as={LuCreditCard} boxSize="4" />
                <Text {...ITEM_TEXT_PROPS}>Address</Text>
                <Icon as={LuPencil} boxSize="4" color="fg.muted" />
              </Menu.Item>
              {addressLines.length > 0 ? (
                <Box {...SUBTEXT_PROPS}>
                  {addressLines.map((line) => (
                    <Text key={line} fontSize="xs" lineHeight="16px">
                      {line}
                    </Text>
                  ))}
                </Box>
              ) : null}
            </Box>

            <Separator borderColor="border" />

            <Menu.Item value="sign-out" cursor="pointer" onSelect={handleSignOut}>
              <Icon as={LuLogOut} boxSize="4" />
              <Text {...ITEM_TEXT_PROPS}>Sign Out</Text>
            </Menu.Item>
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
    <EditPasswordDialog
      open={passwordDialogOpen}
      onOpenChange={setPasswordDialogOpen}
      onSubmit={handlePasswordSubmit}
    />
    <EditAddressDialog
      open={addressDialogOpen}
      onOpenChange={setAddressDialogOpen}
      initialValue={addressInitialValue}
      onSubmit={handleAddressSubmit}
    />
    <input
      ref={fileInputRef}
      type="file"
      accept={FILE_INPUT_ACCEPT}
      onChange={handleAvatarFileChange}
      style={{ display: 'none' }}
    />
    <Toaster toaster={toaster}>
      {(toast) => (
        <Toast.Root>
          <Toast.Title>{toast.title}</Toast.Title>
          {toast.description ? <Toast.Description>{toast.description}</Toast.Description> : null}
          <Toast.CloseTrigger />
        </Toast.Root>
      )}
    </Toaster>
    </>
  );
};
